/* oxlint-disable sort-keys */
import React, { createContext, FC, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useHistory, useLocation } from "react-router-dom";
import ExceptionsService from "@app/exceptions/exceptionsService";
import { DropdownItem, NestingParentDropdownItem } from "@app/shared/checkbox-dropdown/types";
import {
  ExceptionsFilterRequestModel,
  ProjectFilterOptions,
  ServiceAreaFilterOptions,
  TagFilterOptions
} from "@app/exceptions/interfaces";
import { calculateServiceAreaSelections } from "@app/shared/service-area-dropdown/helpers";
import { clone } from "lodash";
import { RegionSetStatusType } from "@app/projects/enumerators";
import { SortDirections } from "@app/shared/waffles/exports";
import MetOverallStatus from "@app/exceptions/metOverallStatusEnum";
import useGapFillStatusDescriptions from "@app/projects/network-assessment/adequacy/hooks/useGapFillStatusDescriptions";
import { GapFillStatusEnum } from "@app/projects/network-assessment/adequacy";
import startCase from "lodash/startCase";
import NetworkTaxonomyService from "@app/network-taxonomies/networkTaxonomyService";
import { NetworkTaxonomyLineOfBusinessSimple } from "@app/network-taxonomies/networkTaxonomy";
import UnprocessedExcludedProviders from "@app/exceptions/unprocessedExcludedProviders";
import { CheckboxStateEnum } from "@app/shared/qes-standards/forms/Checkbox";
import { SpecialtyGroupExceptionReviewStatusFilterOption } from "@app/exceptions/specialtyGroupExceptionReviewStatusFilterOptionEnum";
import { SortExceptionColumn, SpecialtyGroupExceptionGrade, SpecialtyGroupExceptionReviewStatus, SpecialtyGroupExceptionReviewStatusLabels, SpecialtyGroupExceptionStatus } from "@app/exceptions/enums";
import { FilterSetItem } from "@app/resource-filters/filter-sets/filterSetItem";

export interface ExceptionsNavFilter {
  project: DropdownItem<string>;
  statuses: DropdownItem<string>[];
}

export const EXCEPTIONS_FILTERS_SESSION_KEY_OVERVIEW = 'exceptions_filters_overview';
export const EXCEPTIONS_FILTERS_SESSION_KEY_ARCHIVED = 'exceptions_filters_archived';

const getSessionKey = (isForArchived: boolean) =>
    isForArchived ? EXCEPTIONS_FILTERS_SESSION_KEY_ARCHIVED : EXCEPTIONS_FILTERS_SESSION_KEY_OVERVIEW;

const isPageReload = (): boolean => {
    try {
        const entries = performance.getEntriesByType('navigation');
        if (entries.length > 0) return (entries[0] as PerformanceNavigationTiming).type === 'reload';
    } catch { }
    //fallback check
    return performance.navigation?.type === 1;
};

// Captured at render time in ExceptionsFiltersContextProvider (before any effects run).
// False on fresh page load (router effect hasn't fired yet).
// True when navigating within exceptions pages.
let routerIsActive = false;
export const markExceptionsRouterActive = (): void => { routerIsActive = true; };
export const markExceptionsRouterInactive = (): void => { routerIsActive = false; };

interface StoredExceptionsFilters {
  filterEndDate: string;
  filterExceptionGrades: Array<DropdownItem<string>>;
  filterExceptionStatus: Array<DropdownItem<string>>;
  filterGapResolutionStatuses: Array<DropdownItem<string>>;
  filterLinesOfBusiness: Array<DropdownItem<string>>;
  filterProjects: Array<DropdownItem<string>>;
  filterReviewStatus: SpecialtyGroupExceptionReviewStatusFilterOption;
  filterServiceArea: string[];
  filterServiceAreaStatuses: Array<DropdownItem<string>>;
  filterSpecialty: Array<DropdownItem<string>>;
  filterStartDate: string;
  filterTags: string[];
  metOverallStatus: MetOverallStatus;
  unprocessedExcludedProviders: UnprocessedExcludedProviders;
}

const readStoredFilters = (isForArchived: boolean): StoredExceptionsFilters | null => {
  try {
    const raw = sessionStorage.getItem(getSessionKey(isForArchived));
    return raw ? (JSON.parse(raw) as StoredExceptionsFilters) : null;
  } catch {
    return null;
  }
};

const writeStoredFilters = (filters: StoredExceptionsFilters, isForArchived: boolean): void => {
  try {
    sessionStorage.setItem(getSessionKey(isForArchived), JSON.stringify(filters));
  } catch {
    // sessionStorage unavailable — fail silently
  }
};

export const reconcileStoredFilters = (
  stored: StoredExceptionsFilters,
  projectsOptions: DropdownItem<string>[],
  specialtyOptions: DropdownItem<string>[],
  serviceAreaOptions: NestingParentDropdownItem[],
  tagsOptions: NestingParentDropdownItem[],
): StoredExceptionsFilters => {
  const validProjects = new Set(projectsOptions.map(o => o.key));
  const validSpecialties = new Set(specialtyOptions.map(o => o.key));
  const validServiceAreas = new Set(serviceAreaOptions.flatMap(o => o.children.map(c => c.key)));
  const validTags = new Set(tagsOptions.flatMap(o => o.children.map(c => c.key)));
  // filterExceptionStatus is intentionally excluded: session keys are tab-specific (isForArchived),
  // so status options from the Archived tab (e.g., None) can never appear in the Active tab's stored filters.
  return {
    ...stored,
    filterProjects: stored.filterProjects.filter(p => validProjects.has(p.key)),
    filterSpecialty: stored.filterSpecialty.filter(sp => validSpecialties.has(sp.key)),
    filterServiceArea: stored.filterServiceArea.filter(sa => validServiceAreas.has(sa)),
    filterTags: stored.filterTags.filter(tag => validTags.has(tag)),
  };
};

const convertProjectsToDropdownItems = (obj: ProjectFilterOptions): DropdownItem<string>[] => {
  return Object.entries(obj).map(([key, value]) => ({
    key,
    name: value
  }));
};

const convertSpecialtiesToDropdownItems = (arr: { key: string; value: string }[]): DropdownItem<string>[] => {
  return arr.map(item => ({
    key: item.key,
    name: item.value,
  }));
};

const convertLinesOfBusinessToDropdownItems = (linesOfBusiness: NetworkTaxonomyLineOfBusinessSimple[]): DropdownItem<string>[] => {
  return linesOfBusiness.map(line => ({
    key: line.lineOfBusinessId.toString(),
    name: line.name
  }));
};

const convertServiceAreasToNestingParentDropdownItems = (data: {
  [stateAbbreviation: string]: ServiceAreaFilterOptions[]
}): NestingParentDropdownItem[] => {
  return Object.keys(data).map(stateAbbreviation => ({
    key: stateAbbreviation,
    name: stateAbbreviation,
    checkboxStatus: null,
    children: data[stateAbbreviation].map(serviceArea => ({
      key: serviceArea.countyRegionSetId.toString(),
      name: !!serviceArea.countySSA ? serviceArea.countyDisplayName + ' - ' + serviceArea.countySSA : serviceArea.countyDisplayName
    }))
  } as NestingParentDropdownItem));
};

const convertTagsToNestingParentDropdownItems = (data: {
  [tagGroupName: string]: TagFilterOptions[]
}): NestingParentDropdownItem[] => {
  return Object.keys(data).map(tagGroup => ({
    key: tagGroup,
    name: tagGroup,
    checkboxStatus: null as CheckboxStateEnum,
    children: Object.entries(
      data[tagGroup].reduce((acc, tag) => {
        if (!acc[tag.tagName]) {
          acc[tag.tagName] = [];
        }
        acc[tag.tagName].push(tag.tagId);
        return acc;
      }, {} as Record<string, number[]>)
    ).map(([tagName, tagIds]) => ({
      key: JSON.stringify(tagIds),
      name: tagName
    }))
  }));
};

export interface ExceptionsFiltersContextValues {
  applyCurrentFilterSetItems: (filterSetItems: FilterSetItem[]) => void;
  applyFilters: (exceptionRequestModel: ExceptionsFilterRequestModel) => void;
  applyDisabled: boolean;
  clearFilters: () => void;
  reloadFilters: () => void;
  exceptionStatusOptions: Array<DropdownItem<string>>,
  exceptionsLoading: boolean,
  filterEndDate: string;
  filterExceptionStatus: Array<DropdownItem<string>>;
  filterGapResolutionStatuses: Array<DropdownItem<string>>;
  filterReviewStatus: SpecialtyGroupExceptionReviewStatusFilterOption;
  filterExceptionGrades: Array<DropdownItem<string>>;
  filterLinesOfBusiness: Array<DropdownItem<string>>;
  filterProjects: Array<DropdownItem<string>>;
  filterServiceArea: string[];
  filterServiceAreaStatuses: Array<DropdownItem<string>>;
  filterSpecialty: Array<DropdownItem<string>>;
  filterStartDate: string;
  filterTags: string[];
  gapResolutionStatusOptions: Array<DropdownItem<string>>;
  getExceptionsFilter: () => ExceptionsFilterRequestModel;
  exceptionGradeOptions: Array<DropdownItem<string>>;
  hasSetInitialExceptionStatuses: boolean;
  isForArchived: boolean;
  lineOfBusinessOptions: Array<DropdownItem<string>>;
  metOverallStatus: MetOverallStatus;
  pageIndex: number;
  pageSize: number;
  projectsOptions: Array<DropdownItem<string>>;
  refreshUnprocessedExceptionsBanner: boolean;
  requestModel: ExceptionsFilterRequestModel;
  resetToFirstPage: boolean,
  selectedExceptionIds: number[];
  serviceAreaOptions: NestingParentDropdownItem[];
  serviceAreaStatusOptions: Array<DropdownItem<string>>;
  reviewStatusOptions: Array<DropdownItem<string>>,
  setExceptionsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setFilterEndDate: React.Dispatch<React.SetStateAction<string>>;
  setFilterExceptionStatus: React.Dispatch<React.SetStateAction<Array<DropdownItem<string>>>>;
  setFilterGapResolutionStatuses: React.Dispatch<React.SetStateAction<Array<DropdownItem<string>>>>;
  setFilterReviewStatus: React.Dispatch<React.SetStateAction<SpecialtyGroupExceptionReviewStatusFilterOption>>;
  setFilterExceptionGrades: React.Dispatch<React.SetStateAction<Array<DropdownItem<string>>>>;
  setFilterProjects: React.Dispatch<React.SetStateAction<Array<DropdownItem<string>>>>;
  setFilterLinesOfBusiness: React.Dispatch<React.SetStateAction<Array<DropdownItem<string>>>>;
  setFilterServiceArea: React.Dispatch<React.SetStateAction<string[]>>;
  setFilterServiceAreaStatuses: React.Dispatch<React.SetStateAction<Array<DropdownItem<string>>>>;
  setFilterSpecialty: React.Dispatch<React.SetStateAction<Array<DropdownItem<string>>>>;
  setFilterStartDate: React.Dispatch<React.SetStateAction<string>>;
  setFilterTags: React.Dispatch<React.SetStateAction<Array<string>>>;
  setHasSetInitialExceptionStatuses: React.Dispatch<React.SetStateAction<boolean>>;
  setIsForArchived: React.Dispatch<React.SetStateAction<boolean>>;
  setMetOverallStatus: React.Dispatch<React.SetStateAction<MetOverallStatus>>;
  setPageIndex: React.Dispatch<React.SetStateAction<number>>;
  setPageSize: React.Dispatch<React.SetStateAction<number>>;
  setRefreshUnprocessedExceptionsBanner: React.Dispatch<React.SetStateAction<boolean>>;
  setResetToFirstPage: React.Dispatch<React.SetStateAction<boolean>>;
  setSortColumn: React.Dispatch<React.SetStateAction<SortExceptionColumn>>;
  setSortDirection: React.Dispatch<React.SetStateAction<SortDirections>>;
  setRequestModel: React.Dispatch<React.SetStateAction<ExceptionsFilterRequestModel>>;
  setSelectedExceptionIds: React.Dispatch<React.SetStateAction<number[]>>;
  setTriggerRefreshData: React.Dispatch<React.SetStateAction<boolean>>;
  setUnprocessedExcludedProviders: React.Dispatch<React.SetStateAction<UnprocessedExcludedProviders>>;
  sortColumn: SortExceptionColumn;
  sortDirection: SortDirections
  specialtyOptions: Array<DropdownItem<string>>;
  tagsOptions: NestingParentDropdownItem[];
  triggerRefreshData: boolean;
  unprocessedExcludedProviders: UnprocessedExcludedProviders;
  validStartDate: boolean;
  validEndDate: boolean;
  customProjectName: string[];
  setCustomProjectName: (names: string[]) => void;
  enableRegulatoryExceptionReports: boolean[];
  setEnableRegulatoryExceptionReports: (values: boolean[]) => void;
  haveRemovedServiceArea: boolean[];
  setHaveRemovedServiceArea: (values: boolean[]) => void;
  haveRemovedSpecialty: boolean[];
  setHaveRemovedSpecialty: (values: boolean[]) => void;
  selectedIsCciioFlags: boolean[];
  setSelectedIsCciioFlags: (values: boolean[]) => void;
}

const initialValues: ExceptionsFiltersContextValues = {
  applyCurrentFilterSetItems: () => null,
  applyFilters: () => null,
  applyDisabled: false,
  clearFilters: () => null,
  reloadFilters: () => null,
  exceptionStatusOptions: new Array<DropdownItem<string>>(),
  exceptionsLoading: false,
  filterEndDate: '',
  filterExceptionStatus: new Array<DropdownItem<string>>(),
  filterGapResolutionStatuses: new Array<DropdownItem<string>>(),
  filterReviewStatus: null,
  filterExceptionGrades: new Array<DropdownItem<string>>(),
  filterLinesOfBusiness: new Array<DropdownItem<string>>(),
  filterProjects: new Array<DropdownItem<string>>(),
  filterServiceArea: [],
  filterServiceAreaStatuses: [],
  filterSpecialty: new Array<DropdownItem<string>>(),
  filterStartDate: '',
  filterTags: [],
  gapResolutionStatusOptions: new Array<DropdownItem<string>>(),
  getExceptionsFilter: () => null,
  exceptionGradeOptions: new Array < DropdownItem<string>>(),
  hasSetInitialExceptionStatuses: false,
  isForArchived: false,
  lineOfBusinessOptions: new Array<DropdownItem<string>>(),
  metOverallStatus: MetOverallStatus.All,
  pageIndex: null,
  pageSize: null,
  projectsOptions: null,
  refreshUnprocessedExceptionsBanner: true,
  resetToFirstPage: null,
  requestModel: null,
  selectedExceptionIds: [],
  serviceAreaOptions: [],
  serviceAreaStatusOptions: [],
  reviewStatusOptions: new Array<DropdownItem<string>>(),
  setExceptionsLoading: () => null,
  setFilterEndDate: () => null,
  setFilterExceptionStatus: () => null,
  setFilterGapResolutionStatuses: () => null,
  setFilterReviewStatus: () => null,
  setFilterExceptionGrades: () => null,
  setFilterLinesOfBusiness: () => null,
  setFilterProjects: () => null,
  setFilterServiceArea: () => null,
  setFilterServiceAreaStatuses: () => null,
  setFilterSpecialty: () => null,
  setFilterStartDate: () => null,
  setFilterTags: () => null,
  setHasSetInitialExceptionStatuses: () => null,
  setIsForArchived: () => null,
  setMetOverallStatus: () => null,
  setPageIndex: () => null,
  setPageSize: () => null,
  setRefreshUnprocessedExceptionsBanner: () => null,
  setResetToFirstPage: () => null,
  setRequestModel: () => null,
  setSortColumn: () => null,
  setSortDirection: () => null,
  setSelectedExceptionIds: () => null,
  setTriggerRefreshData: () => null,
  setUnprocessedExcludedProviders: () => null,
  sortColumn: SortExceptionColumn.DateUpdated,
  sortDirection: SortDirections.Desc,
  specialtyOptions: new Array<DropdownItem<string>>(),
  tagsOptions: null,
  triggerRefreshData: true,
  unprocessedExcludedProviders: UnprocessedExcludedProviders.All,
  validStartDate: true,
  validEndDate: true,
  customProjectName: [],
  setCustomProjectName: () => {
  },
  enableRegulatoryExceptionReports: [],
  setEnableRegulatoryExceptionReports: () => {
  },
  haveRemovedServiceArea: [],
  setHaveRemovedServiceArea: () => {},
  haveRemovedSpecialty: [],
  setHaveRemovedSpecialty: () => {},
  selectedIsCciioFlags: [],
  setSelectedIsCciioFlags: () => {},
}

export const ExceptionsFiltersContext = createContext(initialValues);

const ExceptionsFiltersContextProvider: FC<{ children: React.ReactNode }> = (props: { children: React.ReactNode }) => {
  /* Constants */
  const defaultExceptionStatuses = useRef([
    SpecialtyGroupExceptionStatus.Pending,
    SpecialtyGroupExceptionStatus.Submitted,
    SpecialtyGroupExceptionStatus.Approved,
    SpecialtyGroupExceptionStatus.Denied,
    SpecialtyGroupExceptionStatus.Appealed,
    SpecialtyGroupExceptionStatus.Expired
  ]).current;

  const defaultServiceAreaStatuses = useRef([
    RegionSetStatusType.Active,
    RegionSetStatusType.Approved,
    RegionSetStatusType.Pending,
    RegionSetStatusType.Opportunity,
    null
  ]).current;

  const defaultGapResolutionStatuses = useRef([
    GapFillStatusEnum.none,
    GapFillStatusEnum.gapFilled,
    GapFillStatusEnum.researching,
    GapFillStatusEnum.contractingGapClosure,
    GapFillStatusEnum.contractingExceptionEligible,
    GapFillStatusEnum.exceptionRequired,
    GapFillStatusEnum.rppoWaiverRequired,
    GapFillStatusEnum.availableEntitiesWillNotContract
  ]).current;

  const defaultExceptionGrades = useRef([
    SpecialtyGroupExceptionGrade.None,
    SpecialtyGroupExceptionGrade.A,
    SpecialtyGroupExceptionGrade.B,
    SpecialtyGroupExceptionGrade.C
  ]).current;

  const defaultReviewStatuses = useRef([
    SpecialtyGroupExceptionReviewStatus.None,
    SpecialtyGroupExceptionReviewStatus.NotReady,
    SpecialtyGroupExceptionReviewStatus.ReadyForReview,
    SpecialtyGroupExceptionReviewStatus.ReadyForSubmission,
  ]).current;

  const history = useHistory();
  const location = useLocation();
  const initialQueryParams = useRef(new URLSearchParams(location.search)).current;
  const initialProjectId = initialQueryParams.get("projectId");
  const initialSpecialtyGroupCode = initialQueryParams.get("specialtyGroupCode");
  const hasInitialQueryParams = !!initialProjectId && !!initialSpecialtyGroupCode;
  const exceptionsService = useRef(new ExceptionsService()).current;
  const networkTaxonomyService = useRef(new NetworkTaxonomyService()).current;
  const gapFillStatusDescriptions = useGapFillStatusDescriptions();
  const wasPageReload = useRef(isPageReload()).current;
  // True when the router was already active before this context mounted (exception pages navigation).
  const sessionWasActive = useRef(routerIsActive).current;
  const hasInitiallyLoaded = useRef(false);

  /* Selected Filter Values (and Related) */
  const [filterServiceArea, setFilterServiceArea] = useState<string[]>(initialValues.filterServiceArea);
  const [filterServiceAreaStatuses, setFilterServiceAreaStatuses] = useState<Array<DropdownItem<string>>>(initialValues.filterServiceAreaStatuses);
  const [filterSpecialty, setFilterSpecialty] = useState<Array<DropdownItem<string>>>(initialValues.filterSpecialty);
  const [filterExceptionStatus, setFilterExceptionStatus] = useState<Array<DropdownItem<string>>>(initialValues.filterExceptionStatus);
  const [filterGapResolutionStatuses, setFilterGapResolutionStatuses] = useState<Array<DropdownItem<string>>>(initialValues.filterGapResolutionStatuses);
  const [filterReviewStatus, setFilterReviewStatus] = useState<SpecialtyGroupExceptionReviewStatusFilterOption>(initialValues.filterReviewStatus);
  const [filterExceptionGrades, setFilterExceptionGrades] = useState<Array<DropdownItem<string>>>(initialValues.filterExceptionGrades);
  const [filterProjects, setFilterProjects] = useState<Array<DropdownItem<string>>>(initialValues.filterProjects);
  const [filterLinesOfBusiness, setFilterLinesOfBusiness] = useState<Array<DropdownItem<string>>>(initialValues.filterLinesOfBusiness);
  const [filterTags, setFilterTags] = useState<Array<string>>(initialValues.filterTags);
  const [filterStartDate, setFilterStartDate] = useState<string>(initialValues.filterStartDate);
  const [filterEndDate, setFilterEndDate] = useState<string>(initialValues.filterEndDate);
  const [validStartDate, setValidStartDate] = useState<boolean>(true);
  const [validEndDate, setValidEndDate] = useState<boolean>(true);
  const [applyDisabled, setApplyDisabled] = useState<boolean>(false);
  const [metOverallStatus, setMetOverallStatus] = useState<MetOverallStatus>(MetOverallStatus.All);
  const [unprocessedExcludedProviders, setUnprocessedExcludedProviders] = useState<UnprocessedExcludedProviders>(UnprocessedExcludedProviders.All);

  /* Miscellaneous */
  const [exceptionsLoading, setExceptionsLoading] = useState(initialValues.exceptionsLoading);
  const [requestModel, setRequestModel] = useState<ExceptionsFilterRequestModel>(null);
  const [triggerRefreshData, setTriggerRefreshData] = useState<boolean>(true);
  const [isForArchived, setIsForArchived] = useState<boolean>(false);
  const prevIsForArchivedRef = useRef(isForArchived);
  const pendingReconcileRef = useRef(false);
  const [pageIndex, setPageIndex] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(100);
  const [resetToFirstPage, setResetToFirstPage] = useState<boolean>(false);
  const [sortDirection, setSortDirection] = useState<SortDirections>(SortDirections.Desc);
  const [sortColumn, setSortColumn] = useState<SortExceptionColumn>(SortExceptionColumn.DateUpdated);
  const [refreshUnprocessedExceptionsBanner, setRefreshUnprocessedExceptionsBanner] = useState<boolean>(true);

  /* Internal State Values */
  const [hasSetInitialExceptionStatuses, setHasSetInitialExceptionStatuses] = useState(false);
  const [selectedExceptionIds, setSelectedExceptionIds] = useState<number[]>([]);
  const [customProjectName, setCustomProjectName] = useState<string[]>([]);
  const [enableRegulatoryExceptionReports, setEnableRegulatoryExceptionReports] = useState<boolean[]>([]);
  const [haveRemovedServiceArea, setHaveRemovedServiceArea] = useState<boolean[]>([]);
  const [haveRemovedSpecialty, setHaveRemovedSpecialty] = useState<boolean[]>([]);
  const [selectedIsCciioFlags, setSelectedIsCciioFlags] = useState<boolean[]>([]);

  /* Filter Options */
  const queryClient = useQueryClient();

  const { data: filterOptionsData, isFetching, isError: isFilterOptionsError } = useQuery({
    queryKey: ['exceptions-filter-options', isForArchived],
    queryFn: () => exceptionsService.getFilterOptions(isForArchived),
    staleTime: 5 * 60 * 1000,
  });

  const { data: linesOfBusinessData, isError: isLinesOfBusinessError } = useQuery({
    queryKey: ['exceptions-lines-of-business'],
    queryFn: () => networkTaxonomyService.getClientFriendlyLinesOfBusiness(),
    staleTime: Infinity,
  });

  const projectsOptions = useMemo(() =>
    filterOptionsData ? convertProjectsToDropdownItems(filterOptionsData.bulkProjects) : [],
  [filterOptionsData]);

  const specialtyOptions = useMemo(() =>
    filterOptionsData ? convertSpecialtiesToDropdownItems(filterOptionsData.bulkSpecialties) : [],
  [filterOptionsData]);

  const serviceAreaOptions = useMemo(() =>
    filterOptionsData ? convertServiceAreasToNestingParentDropdownItems(filterOptionsData.bulkServiceAreas) : [],
  [filterOptionsData]);

  const tagsOptions = useMemo(() =>
    filterOptionsData ? convertTagsToNestingParentDropdownItems(filterOptionsData.bulkTags) : [],
  [filterOptionsData]);

  const serviceAreaStatusOptions = useMemo(() =>
    defaultServiceAreaStatuses.map(status => ({
      key: status,
      name: status == null ? 'Blank' : status.charAt(0).toUpperCase() + status.slice(1)
    } as DropdownItem<string>)),
  []);

  const exceptionStatusOptions = useMemo(() => {
    const defaultStatuses = clone(defaultExceptionStatuses);
    if (isForArchived) defaultStatuses.unshift(SpecialtyGroupExceptionStatus.None);
    return defaultStatuses.map(status => ({
      key: status,
      name: status.charAt(0).toUpperCase() + status.slice(1)
    } as DropdownItem<string>));
  }, [isForArchived]);

  const reviewStatusOptions = useMemo(() =>
    defaultReviewStatuses.map(status => ({
      key: status,
      name: status === SpecialtyGroupExceptionReviewStatus.None ? '---' : SpecialtyGroupExceptionReviewStatusLabels[status]
    } as DropdownItem<string>)),
  []);

  const lineOfBusinessOptions = useMemo(() =>
    linesOfBusinessData ? convertLinesOfBusinessToDropdownItems(linesOfBusinessData) : [],
  [linesOfBusinessData]);

  const gapResolutionStatusOptions = useMemo(() =>
    defaultGapResolutionStatuses.map(status => ({ key: status, name: gapFillStatusDescriptions[status] } as DropdownItem<string>)),
    [gapFillStatusDescriptions]);
  const exceptionGradeOptions = useMemo(() =>
    defaultExceptionGrades.map(grade => ({ key: grade, name: startCase(grade) } as DropdownItem<string>)),
  []);

  useEffect(() => {
    if (isFilterOptionsError) {
      toast("Failed to load exception filter options.", { containerId: "z-index-9999", type: "error" });
    }
  }, [isFilterOptionsError]);

  useEffect(() => {
    if (isLinesOfBusinessError) {
      toast("Failed to load lines of business.", { containerId: "z-index-9999", type: "error" });
    }
  }, [isLinesOfBusinessError]);

  useEffect(() => {
    if (!pendingReconcileRef.current || isFetching || !filterOptionsData) return;
    pendingReconcileRef.current = false;

    const validProjectKeys = new Set(projectsOptions.map(o => o.key));
    const validSpecialtyKeys = new Set(specialtyOptions.map(o => o.key));
    const validServiceAreaKeys = new Set(serviceAreaOptions.flatMap(o => o.children.map(c => c.key)));
    const validTagKeys = new Set(tagsOptions.flatMap(o => o.children.map(c => c.key)));

    setFilterProjects(prev => prev.filter(p => validProjectKeys.has(p.key)));
    setFilterSpecialty(prev => prev.filter(sp => validSpecialtyKeys.has(sp.key)));
    setFilterServiceArea(prev => prev.filter(sa => validServiceAreaKeys.has(sa)));
    setFilterTags(prev => prev.filter(tag => validTagKeys.has(tag)));
  }, [filterOptionsData, isFetching, projectsOptions, serviceAreaOptions, specialtyOptions, tagsOptions]);

  useEffect(() => {
    if (filterOptionsData && linesOfBusinessData && !isFetching) {

      if (hasInitialQueryParams && !isForArchived) {
        const matchedProject = projectsOptions.find(p => p.key === initialProjectId);
        const matchedSpecialty = specialtyOptions.find(s => s.key === initialSpecialtyGroupCode);
        const defaults = defaultFilterSelections;

        setFilterProjects(matchedProject ? [matchedProject] : defaults.selectedProjects);
        setFilterSpecialty(matchedSpecialty ? [matchedSpecialty] : defaults.selectedSpecialties);
        setFilterServiceArea(defaults.selectedServiceAreas);
        setFilterServiceAreaStatuses(defaults.selectedServiceAreaStatuses);
        setFilterExceptionStatus(defaults.selectedStatuses);
        setFilterGapResolutionStatuses(defaults.selectedGapResolutionStatuses);
        setFilterReviewStatus(defaults.selectedReviewStatus);
        setFilterExceptionGrades(defaults.selectedExceptionGrades);
        setFilterLinesOfBusiness(defaults.selectedLinesOfBusiness);
        setFilterTags(defaults.selectedTags);
        setFilterStartDate(defaults.selectedStartDate);
        setFilterEndDate(defaults.selectedEndDate);
        setMetOverallStatus(defaults.selectedMetOverall);
        setUnprocessedExcludedProviders(defaults.selectedUnprocessedExcludedProviders);
        setHasSetInitialExceptionStatuses(true);
        return;
      }

      const isTabSwitch = prevIsForArchivedRef.current !== isForArchived;
      prevIsForArchivedRef.current = isForArchived;
      const isInitialLoad = !hasInitiallyLoaded.current;
      if (!isInitialLoad && !isTabSwitch) return;
      hasInitiallyLoaded.current = true;
      const freshNavigation = isInitialLoad && !wasPageReload && !sessionWasActive;

      if (freshNavigation) {
        sessionStorage.removeItem(EXCEPTIONS_FILTERS_SESSION_KEY_OVERVIEW);
        sessionStorage.removeItem(EXCEPTIONS_FILTERS_SESSION_KEY_ARCHIVED);
      }

      const stored = !freshNavigation ? readStoredFilters(isForArchived) : null;

      if (stored) {
        const filtersToApply = !isInitialLoad
          ? reconcileStoredFilters(stored, projectsOptions, specialtyOptions, serviceAreaOptions, tagsOptions)
          : stored;
        setFilterServiceArea(filtersToApply.filterServiceArea);
        setFilterServiceAreaStatuses(filtersToApply.filterServiceAreaStatuses);
        setFilterSpecialty(filtersToApply.filterSpecialty);
        setFilterExceptionStatus(filtersToApply.filterExceptionStatus);
        setFilterProjects(filtersToApply.filterProjects);
        setFilterStartDate(filtersToApply.filterStartDate);
        setFilterEndDate(filtersToApply.filterEndDate);
        setMetOverallStatus(filtersToApply.metOverallStatus);
        setUnprocessedExcludedProviders(filtersToApply.unprocessedExcludedProviders);
        setFilterGapResolutionStatuses(filtersToApply.filterGapResolutionStatuses);
        setFilterReviewStatus(filtersToApply.filterReviewStatus);
        setFilterExceptionGrades(filtersToApply.filterExceptionGrades);
        setFilterLinesOfBusiness(filtersToApply.filterLinesOfBusiness);
        setFilterTags(filtersToApply.filterTags);
      }
      else {
        const defaults = defaultFilterSelections;

        setFilterServiceArea(defaults.selectedServiceAreas);
        setFilterServiceAreaStatuses(defaults.selectedServiceAreaStatuses);
        setFilterSpecialty(defaults.selectedSpecialties);
        setFilterExceptionStatus(defaults.selectedStatuses);
        setFilterProjects(defaults.selectedProjects);
        setFilterStartDate(defaults.selectedStartDate);
        setFilterEndDate(defaults.selectedEndDate);
        setMetOverallStatus(defaults.selectedMetOverall);
        setUnprocessedExcludedProviders(defaults.selectedUnprocessedExcludedProviders);
        setFilterGapResolutionStatuses(defaults.selectedGapResolutionStatuses);
        setFilterReviewStatus(defaults.selectedReviewStatus);
        setFilterExceptionGrades(defaults.selectedExceptionGrades);
        setFilterLinesOfBusiness(defaults.selectedLinesOfBusiness);
        setFilterTags(defaults.selectedTags);

        const navFilter = (history.location.state as { exceptionsNavFilter?: ExceptionsNavFilter } | null)?.exceptionsNavFilter;

        if (navFilter) {
          setFilterProjects([navFilter.project]);
          setFilterExceptionStatus(navFilter.statuses);
          history.replace(history.location.pathname, undefined);
        }
      }
      setHasSetInitialExceptionStatuses(true);
    }
  }, [filterOptionsData, linesOfBusinessData, isFetching, isForArchived]);

  // because we auto-set exception status filters on load, we have to apply them
  // isForArchived: filterExceptionStatus is always [] on Archived tab (status is hardcoded in getExceptionsFilter)
  useEffect(() => {
    if (hasSetInitialExceptionStatuses && (filterExceptionStatus.length > 0 || isForArchived)) {
      applyFilters(getExceptionsFilter());
    }
  }, [hasSetInitialExceptionStatuses]);

  useEffect(() => {
    if (hasSetInitialExceptionStatuses && (filterExceptionStatus.length > 0 || isForArchived)) {
      applyFilters(getExceptionsFilter());
    }
  }, [pageIndex, pageSize, sortDirection, sortColumn]);

  // if we've got a start date with no end date, or a start date that's before the end date, or an empty start date, it's valid
  useEffect(() => {
    if ((filterStartDate && !filterEndDate) || (filterStartDate && filterEndDate && filterStartDate < filterEndDate) || (!filterStartDate)) {
      setValidStartDate(true);
    } else {
      setValidStartDate(false);
    }
  }, [filterStartDate, filterEndDate]);

  // if we've got an end date with no start date, or an end date that's after the start date, or an empty end date, it's valid
  useEffect(() => {
    if ((filterEndDate && !filterStartDate) || (filterEndDate && filterStartDate && filterEndDate > filterStartDate) || (!filterEndDate)) {
      setValidEndDate(true);
    } else {
      setValidEndDate(false);
    }
  }, [filterEndDate, filterStartDate]);

  useEffect(() => {
    if (filterProjects.length === 0
      || filterServiceArea.length === 0
      || filterServiceAreaStatuses.length === 0
      || filterSpecialty.length === 0
      || !isForArchived && filterExceptionStatus.length === 0
      || filterGapResolutionStatuses.length === 0
      || filterExceptionGrades.length === 0
      || filterLinesOfBusiness.length === 0
      || filterTags.length === 0
      || !validStartDate
      || !validEndDate) {
      setApplyDisabled(true)
    } else {
      setApplyDisabled(false);
    }
  }, [filterProjects, filterServiceArea, filterServiceAreaStatuses, filterSpecialty, filterExceptionStatus, validStartDate, validEndDate, filterGapResolutionStatuses, filterExceptionGrades, filterLinesOfBusiness, filterTags]);

  useEffect(() => {
    if (!hasSetInitialExceptionStatuses || !routerIsActive || isFetching) return;
    writeStoredFilters({
      filterEndDate,
      filterExceptionGrades,
      filterExceptionStatus,
      filterGapResolutionStatuses,
      filterLinesOfBusiness,
      filterProjects,
      filterReviewStatus,
      filterServiceArea,
      filterServiceAreaStatuses,
      filterSpecialty,
      filterStartDate,
      filterTags,
      metOverallStatus,
      unprocessedExcludedProviders,
    }, isForArchived);
  }, [hasSetInitialExceptionStatuses, isFetching, isForArchived, filterEndDate, filterExceptionGrades, filterExceptionStatus, filterGapResolutionStatuses, filterLinesOfBusiness, filterProjects, filterReviewStatus, filterServiceArea, filterServiceAreaStatuses, filterSpecialty, filterStartDate, filterTags, metOverallStatus, unprocessedExcludedProviders]);

  // T is expected to be DropdownItem or string
  const getDefaultSelectedDropdownItems = <T, >(options: T[], selectedOptions: string[], selectAll?: boolean) => {
    if (options.length > 0) {
      if (selectAll) {
        return options;
      }
      // if it's a DropdownItem
      else if (typeof options[0] === "object") {
        return options.filter((s) => selectedOptions.includes((s as DropdownItem<string>).key)) as T[];
        // if it's a string
      } else {
        return options.filter((s) => selectedOptions.includes(s as string)) as T[];
      }
    }
    return new Array<T>();
  };

  const getDefaultSelectedNestedDropdownItems = (options: NestingParentDropdownItem[], selectedOptions: string[], selectAll?: boolean) => {
    if (selectAll) {
      return options.flatMap((t) => t.children.map((c) => c.key));
    }

    return options.flatMap((t) => {
      return t.children.filter((c) => selectedOptions.includes(c.key)).map((c) => c.key);
    });
  };

  const applyFilters = (exceptionRequestModel: ExceptionsFilterRequestModel) => {
    setExceptionsLoading(true);
    setTriggerRefreshData(true);

    // trigger data refresh
    setRequestModel(exceptionRequestModel);
  }

  const getExceptionsFilter = () : ExceptionsFilterRequestModel => {
    const serviceArea = calculateServiceAreaSelections(serviceAreaOptions, filterServiceArea, true);
    const counties = serviceArea.counties;

    return {
      projectIds: (filterProjects.length > 0 ? filterProjects : projectsOptions).map((p) => parseInt(p.key, 10)),
      regionSetIds: counties.map((c) => parseInt(c, 10)),
      specialtyGroupCodes: filterSpecialty.map((sp) => sp.key),
      serviceAreaStatuses: filterServiceAreaStatuses.map(sa => sa.key as RegionSetStatusType),
      startDate: filterStartDate ? filterStartDate : null,
      endDate: filterEndDate ? filterEndDate : null,
      specialtyGroupExceptionStatus: isForArchived ? [SpecialtyGroupExceptionStatus.Archived] : filterExceptionStatus.map((s) => s.key as SpecialtyGroupExceptionStatus),
      metOverall: metOverallStatus,
      gapResolutionStatuses: filterGapResolutionStatuses.map((s) => s.key),
      reviewStatus: filterReviewStatus === SpecialtyGroupExceptionReviewStatusFilterOption.All ? null : filterReviewStatus,
      specialtyGroupExceptionGrades: filterExceptionGrades.map(eg => eg.key as SpecialtyGroupExceptionGrade),
      pageSize: pageSize,
      pageIndex: pageIndex,
      sortExceptionColumn: sortColumn,
      sortDirection: sortDirection,
      lineOfBusinessIds: filterLinesOfBusiness.map((line) => parseInt(line.key)),
      tagIds: filterTags.flatMap(tagList => JSON.parse(tagList).map((tag: string) => parseInt(tag, 10))),
      unprocessedExcludedProviders : unprocessedExcludedProviders === UnprocessedExcludedProviders.All ? undefined : unprocessedExcludedProviders === UnprocessedExcludedProviders.Yes,
    };
  }

  const clearFilters = () => {
    sessionStorage.removeItem(getSessionKey(isForArchived));
    const defaults = defaultFilterSelections;

    setFilterServiceArea(defaults.selectedServiceAreas);
    setFilterServiceAreaStatuses(defaults.selectedServiceAreaStatuses);
    setFilterSpecialty(defaults.selectedSpecialties);
    setFilterExceptionStatus(defaults.selectedStatuses);
    setFilterProjects(defaults.selectedProjects);
    setFilterStartDate(defaults.selectedStartDate);
    setFilterEndDate(defaults.selectedEndDate);
    setMetOverallStatus(defaults.selectedMetOverall);
    setFilterGapResolutionStatuses(defaults.selectedGapResolutionStatuses);
    setFilterReviewStatus(defaults.selectedReviewStatus);
    setFilterExceptionGrades(defaults.selectedExceptionGrades);
    setFilterLinesOfBusiness(defaults.selectedLinesOfBusiness);
    setFilterTags(defaults.selectedTags);
    setUnprocessedExcludedProviders(defaults.selectedUnprocessedExcludedProviders)

    applyFilters(getEmptyDefaultFilters());
  }

  const getEmptyDefaultFilters = () : ExceptionsFilterRequestModel => {
    // handling all this up front here so we only have to check emptyFilters once instead of 5 times
    const emptyDefaults = defaultFilterSelections;

    const serviceArea = calculateServiceAreaSelections(serviceAreaOptions, emptyDefaults.selectedServiceAreas, true);
    const counties = serviceArea.counties;
    
    return {
      projectIds: emptyDefaults.selectedProjects.map((p) => parseInt(p.key, 10)),
      regionSetIds: counties.map((c) => parseInt(c, 10)),
      specialtyGroupCodes: emptyDefaults.selectedSpecialties.map((sp) => sp.key),
      serviceAreaStatuses: emptyDefaults.selectedServiceAreaStatuses.map(sa => sa.key as RegionSetStatusType),
      startDate: null,
      endDate: null,
      specialtyGroupExceptionStatus: isForArchived ? [SpecialtyGroupExceptionStatus.Archived] : emptyDefaults.selectedStatuses.map((s) => s.key as SpecialtyGroupExceptionStatus),
      metOverall: emptyDefaults.selectedMetOverall,
      gapResolutionStatuses: emptyDefaults.selectedGapResolutionStatuses.map((sgrs) => sgrs.key),
      reviewStatus: emptyDefaults.selectedReviewStatus === SpecialtyGroupExceptionReviewStatusFilterOption.All ? null : emptyDefaults.selectedReviewStatus,
      specialtyGroupExceptionGrades: emptyDefaults.selectedExceptionGrades.map(eg => eg.key as SpecialtyGroupExceptionGrade),
      pageSize: pageSize,
      pageIndex: pageIndex,
      sortExceptionColumn: sortColumn,
      sortDirection: sortDirection,
      lineOfBusinessIds: emptyDefaults.selectedLinesOfBusiness.map((line) => parseInt(line.key)),
      tagIds: emptyDefaults.selectedTags.flatMap(tagList => JSON.parse(tagList).map((tag: string) => parseInt(tag, 10))),
      unprocessedExcludedProviders : emptyDefaults.selectedUnprocessedExcludedProviders === UnprocessedExcludedProviders.All ? undefined : emptyDefaults.selectedUnprocessedExcludedProviders === UnprocessedExcludedProviders.Yes,
    };
  }

  const reloadFilters = () => {
    pendingReconcileRef.current = true;
    // Clear the other tab's session key — its cached selections may reference options that no longer exist.
    sessionStorage.removeItem(getSessionKey(!isForArchived));
    queryClient.invalidateQueries({ queryKey: ['exceptions-filter-options'] });
  }

  const applyCurrentFilterSetItems = (filterSetItems: FilterSetItem[]) => {
    setFilterProjects(filterSetItems.filter(item => item.namespacePath.startsWith("Project§"))
      .map(item => projectsOptions.find(project => project.key === item.displayName))
      .filter(item => item));
    setFilterServiceArea(filterSetItems.filter(item => item.namespacePath.startsWith("ServiceArea§")
      && serviceAreaOptions.some(serviceArea => serviceArea.children.some(serviceArea => serviceArea.key === item.displayName)))
      .map(item => item.displayName));
    setFilterSpecialty(filterSetItems.filter(item => item.namespacePath.startsWith("Specialty§"))
      .flatMap(item => specialtyOptions.filter(specialty => specialty.key === item.displayName))
      .filter(item => item));
    setFilterServiceAreaStatuses(filterSetItems.filter(item => item.namespacePath.startsWith("ServiceAreaStatus§"))
      .map(item => serviceAreaStatusOptions.find(serviceAreaStatus => serviceAreaStatus.key === item.displayName || !serviceAreaStatus.key && item.displayName === 'Blank'))
      .filter(item => item));
    const tagIdsFromSavedFilter = new Set(filterSetItems.filter(item => item.namespacePath.startsWith("Tag§"))
      .flatMap(item => { try { return JSON.parse(item.displayName) as number[]; } catch { return []; } }));
    setFilterTags(
      tagsOptions.flatMap(tagGroup =>
        tagGroup.children
          .filter(tag => { try { return (JSON.parse(tag.key) as number[]).some(tagId => tagIdsFromSavedFilter.has(tagId)); } catch { return false; } })
          .map(tag => tag.key)
      )
    );
    if (!isForArchived) {
      setFilterExceptionStatus(filterSetItems.filter(item => item.namespacePath.startsWith("ExceptionStatus§"))
        .map(item => exceptionStatusOptions.find(exceptionStatus => exceptionStatus.key === item.displayName))
        .filter(item => item));
    } else {
      setFilterExceptionStatus([]);
    }
    setFilterGapResolutionStatuses(filterSetItems.filter(item => item.namespacePath.startsWith("GapResolutionStatus§"))
      .map(item => gapResolutionStatusOptions.find(gapResolutionStatus => gapResolutionStatus.key === item.displayName))
      .filter(item => item));
    const reviewStatus = filterSetItems.find(item => item.namespacePath.startsWith("ReviewStatus§"));
    setFilterReviewStatus(reviewStatus?.displayName as SpecialtyGroupExceptionReviewStatusFilterOption);
    const startDate = filterSetItems.find(item => item.namespacePath.startsWith("StartDate§"));
    setFilterStartDate(startDate?.displayName ?? '');
    const endDate = filterSetItems.find(item => item.namespacePath.startsWith("EndDate§"));
    setFilterEndDate(endDate?.displayName ?? '');
    const metOverall = filterSetItems.find(item => item.namespacePath.startsWith("MetOverallStatus§"));
    setMetOverallStatus(metOverall?.displayName as MetOverallStatus ?? MetOverallStatus.All);
    setFilterExceptionGrades(filterSetItems.filter(item => item.namespacePath.startsWith("ExceptionGrade§"))
      .map(item => exceptionGradeOptions.find(exceptionGrade => exceptionGrade.key == item.displayName))
      .filter(item => item));
    setFilterLinesOfBusiness(filterSetItems.filter(item => item.namespacePath.startsWith("LineOfBusiness§"))
      .map(item =>  lineOfBusinessOptions.find(line => line.key === item.displayName))
      .filter(item => item));
    const excludedProviders = filterSetItems.find(item => item.namespacePath.startsWith("UnprocessedExcludedProviders§"));
    setUnprocessedExcludedProviders(excludedProviders?.displayName as UnprocessedExcludedProviders ?? UnprocessedExcludedProviders.All);
  }

  const defaultFilterSelections = useMemo(() => {
    const selectedServiceAreas = getDefaultSelectedNestedDropdownItems(serviceAreaOptions, [], true);
    const selectedServiceAreaStatuses = getDefaultSelectedDropdownItems(serviceAreaStatusOptions, defaultServiceAreaStatuses, true);
    const selectedSpecialties = getDefaultSelectedDropdownItems(specialtyOptions, [], true);
    const selectedStatuses = getDefaultSelectedDropdownItems(exceptionStatusOptions, defaultExceptionStatuses, true);
    const selectedProjects = getDefaultSelectedDropdownItems(projectsOptions, [], true);
    const selectedStartDate = '';
    const selectedEndDate = '';
    const selectedGapResolutionStatuses = getDefaultSelectedDropdownItems(gapResolutionStatusOptions, defaultGapResolutionStatuses, true);
    const selectedReviewStatus = SpecialtyGroupExceptionReviewStatusFilterOption.All;
    const selectedMetOverall = MetOverallStatus.All;
    const selectedExceptionGrades = getDefaultSelectedDropdownItems(exceptionGradeOptions, defaultExceptionGrades, true);
    const selectedLinesOfBusiness = getDefaultSelectedDropdownItems(lineOfBusinessOptions, [], true);
    const selectedTags = getDefaultSelectedNestedDropdownItems(tagsOptions, [], true);
    const selectedUnprocessedExcludedProviders = UnprocessedExcludedProviders.All;

    return {
      selectedServiceAreas,
      selectedServiceAreaStatuses,
      selectedSpecialties,
      selectedStatuses,
      selectedProjects,
      selectedStartDate,
      selectedEndDate,
      selectedMetOverall,
      selectedGapResolutionStatuses,
      selectedReviewStatus,
      selectedExceptionGrades,
      selectedLinesOfBusiness,
      selectedTags,
      selectedUnprocessedExcludedProviders,
    };
  }, [serviceAreaOptions, serviceAreaStatusOptions, specialtyOptions, exceptionStatusOptions, projectsOptions, gapResolutionStatusOptions, lineOfBusinessOptions, tagsOptions]);

  return (
    <ExceptionsFiltersContext.Provider
      value={{
        applyCurrentFilterSetItems,
        applyDisabled,
        applyFilters,
        clearFilters,
        reloadFilters,
        exceptionStatusOptions,
        exceptionsLoading,
        filterEndDate,
        filterExceptionStatus,
        filterGapResolutionStatuses,
        filterReviewStatus,
        filterExceptionGrades,
        filterLinesOfBusiness,
        filterProjects,
        filterServiceArea,
        filterServiceAreaStatuses,
        filterSpecialty,
        filterStartDate,
        filterTags,
        gapResolutionStatusOptions,
        getExceptionsFilter,
        exceptionGradeOptions,
        hasSetInitialExceptionStatuses,
        isForArchived,
        lineOfBusinessOptions,
        metOverallStatus,
        pageIndex,
        pageSize,
        projectsOptions,
        refreshUnprocessedExceptionsBanner,
        resetToFirstPage,
        requestModel,
        selectedExceptionIds,
        setSelectedExceptionIds,
        serviceAreaOptions,
        serviceAreaStatusOptions,
        reviewStatusOptions,
        setFilterEndDate,
        setExceptionsLoading,
        setFilterExceptionStatus,
        setFilterExceptionGrades,
        setFilterGapResolutionStatuses,
        setFilterReviewStatus,
        setFilterLinesOfBusiness,
        setFilterProjects,
        setFilterServiceArea,
        setFilterServiceAreaStatuses,
        setFilterStartDate,
        setFilterSpecialty,
        setFilterTags,
        setHasSetInitialExceptionStatuses,
        setIsForArchived,
        setMetOverallStatus,
        setPageIndex,
        setPageSize,
        setRefreshUnprocessedExceptionsBanner,
        setResetToFirstPage,
        setRequestModel,
        setSortColumn,
        setSortDirection,
        setTriggerRefreshData,
        setUnprocessedExcludedProviders,
        sortColumn,
        sortDirection,
        specialtyOptions,
        tagsOptions,
        triggerRefreshData,
        unprocessedExcludedProviders,
        validStartDate,
        validEndDate,
        customProjectName,
        setCustomProjectName,
        enableRegulatoryExceptionReports,
        setEnableRegulatoryExceptionReports,
        haveRemovedServiceArea,
        setHaveRemovedServiceArea,
        haveRemovedSpecialty,
        setHaveRemovedSpecialty,
        selectedIsCciioFlags,
        setSelectedIsCciioFlags
      }}>
      {props.children}
    </ExceptionsFiltersContext.Provider>
  )
}

export default ExceptionsFiltersContextProvider;
